import React, { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import jwt_decode from "jwt-decode";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { Button } from "components/button";
import { ProfileUpload } from "components/file-upload";
import { Input } from "components/input";
import { SelectInput } from "components/select";
import { DashboardWrapper } from "container/dashboard-wrapper";
import { MainContentContainer } from "container/dashboard-wrapper/dashboard-wrapper.style";
import { sortedCountries } from "container/study-abroad-admin/mocks";
import { useNotify } from "hooks";
import { fetchProfile } from "services/profile";
import { uploadImage } from "services/school";
import { updateProfile } from "services/users";
import { FormWrapper } from "styles/form";
import { TopSection, ProfileDetails, FormContainer, InputWrapper } from "styles/parent-dashboard";
import { getSession } from "utils/auth";
import { getToken } from "utils/storage";

export interface IProfileData {
   firstName: string;
   lastName: string;
   phoneNumber: string;
   imageUrl?: string;
}

const schema = yup.object().shape({
   firstName: yup.string().required("First name is required"),
   lastName: yup.string().required("Last name is required"),
   phoneNumber: yup
      .string()
      .required("Phone number is a required field")
      .matches(/^[0-9]+$/, "Must be only digits")
      .min(11, "Must be at least 11 digits")
      .max(11, "Must not exceed 11 digits"),
});

const User = ({ details }) => {
   const { notify } = useNotify();
   const [isLoading, setIsLoading] = useState(false);
   const [image, setImage] = useState<Blob[]>([]);
   const user = details?.data?.data;
   const [imageUrl, setImageUrl] = useState<string>(user?.imageUrl);
   const [isUploading, setIsUploading] = useState<boolean>(false);

   const token = getToken();
   const decoded: any = token && jwt_decode(token);

   const {
      handleSubmit,
      register,
      formState: { errors, isDirty, isValid },
   } = useForm<IProfileData>({
      resolver: yupResolver(schema),
      mode: "onChange",
   });

   const onImageChange = async (file: Blob) => {
      if (file) {
         try {
            setIsUploading(true);
            const imgFormData = new FormData();
            imgFormData.append("file", file);
            setImage([file]);
            const response: any = await uploadImage(imgFormData);
            notify({
               message: response.result.message,
               variant: "success",
            });
            setImageUrl(response.result.data.url);
            setIsUploading(false);
         } catch (err: any) {
            setIsUploading(false);
            notify({
               message: err.result.message,
               variant: "error",
            });
         }
      }
   };

   async function onSubmit(values: IProfileData): Promise<void> {
      try {
         setIsLoading(true);
         if (imageUrl) values.imageUrl = imageUrl;
         const result: any = await updateProfile(values);
         const { data } = result;
         setIsLoading(false);
         notify({
            message: data.message,
            variant: "success",
         });
      } catch (error: any) {
         setIsLoading(false);
         notify({
            message: error?.result?.message || error?.result?.custom_message,
            variant: "error",
         });
      }
   }
   return (
      <DashboardWrapper>
         <MainContentContainer>
            <FormWrapper>
               <TopSection>
                  <ProfileUpload
                     file={image}
                     onChange={onImageChange}
                     isLoading={isUploading}
                     imageUrl={imageUrl}
                  />
                  <ProfileDetails>
                     <span className="profile-name">
                        {user?.firstName} {user?.lastName}
                     </span>
                     <br />
                     <span className="profile-location">{user?.email}</span>
                     {/* <Button className="edit-profile-btn" variant="primary">
                        Edit profile
                    </Button> */}
                  </ProfileDetails>
               </TopSection>
               <FormContainer>
                  <form onSubmit={handleSubmit(onSubmit)}>
                     <InputWrapper className="grid-2-2">
                        <Input
                           label="First Name"
                           id="firstName"
                           type="text"
                           {...register("firstName")}
                           error={errors.firstName?.message}
                           defaultValue={user?.firstName}
                        />
                        <Input
                           label="Last Name"
                           id="lastName"
                           type="text"
                           {...register("lastName")}
                           error={errors.lastName?.message}
                           defaultValue={user?.lastName}
                        />
                     </InputWrapper>
                     <InputWrapper className="grid-2-2">
                        <Input
                           label="Phone Number"
                           id="phoneNumber"
                           type="text"
                           {...register("phoneNumber")}
                           error={errors.phoneNumber?.message}
                           defaultValue={user?.phoneNumber}
                        />
                        <SelectInput
                           type="text"
                           label="Country"
                           id="country"
                           options={sortedCountries}
                           value={sortedCountries.find(
                              (option) => option.value.toLowerCase() === decoded?.country
                           )}
                           isDisabled
                        />
                     </InputWrapper>
                     <Button
                        disabled={!isDirty || !isValid || isLoading}
                        type="submit"
                        variant="primary"
                        isLoading={isLoading}
                     >
                        Done
                     </Button>
                  </form>
               </FormContainer>
            </FormWrapper>
         </MainContentContainer>
      </DashboardWrapper>
   );
};

export const getServerSideProps: GetServerSideProps = async (ctx: GetServerSidePropsContext) => {
   const { token } = getSession(ctx);
   const details = await fetchProfile(token);
   return { props: { details } };
};

export default User;
